import { formatBgNumber } from "@sveabank/cc-common";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { usePackage } from "../../hooks/package";
import { useApplicationId } from "../../hooks/useApplicationId";
import { usePages } from "../../hooks/usePages";
import { useSteps } from "../../hooks/useSteps";
import {
	useConfigureCheckCreditMutation,
	useConfigureCreditProductsMutation,
} from "../../services/application";
import {
	useRemoveAddonMutation,
	useRemoveAndSelectPackageMutation,
	useRemovePackageMutation,
	useSelectAddonMutation,
	useSelectAndConfigureBankGiroMutation,
	useSelectPackageMutation,
} from "../../services/package";
import { useFetchPackageSignersQuery } from "../../services/package-agreement";
import type { BankGiroConfig, BankGiroFormValues } from "../../types/bankgiro";
import type { CreditProductConfig } from "../../types/product";
import type { ConfigureBankGiroProductStep, Step } from "../../types/step";
import { stepIs } from "../../utils/application";

const getCurrentBankgiroFormValues = (steps: Step[] = []): BankGiroFormValues => {
	const config = steps.find(
		(s): s is ConfigureBankGiroProductStep =>
			s.name === "ConfigureBankGiroProductStep" && s.status === "Completed",
	)?.state.configuration;

	if (config) {
		const returnConfig: BankGiroFormValues =
			typeof config !== "string"
				? {
						answer: "transferExisting",
						existingNumber: formatBgNumber(config.transferExisting || ""),
					}
				: { answer: config, existingNumber: null };
		return returnConfig;
	}

	return { answer: "createNew" };
};

export const useChoosePackage = () => {
	const applicationId = useApplicationId();
	const navigate = useNavigate();
	const {
		selectedPackageId = null,
		selectedAddons,
		availablePackages,
		availablePackagesIsLoading,
		selectedCreditProducts,
		checkCreditProductBrackets,
		selectedPackageFee = null,
		selectedProducts,
	} = usePackage();

	const { steps, isLoading: isStepsLoading, areStepsCompleted } = useSteps();

	const [_, { isLoading: selectPackageIsLoading }] = useSelectPackageMutation();

	const creditScore = steps?.find(stepIs("CreditScoreStep"))?.state;
	const { nextPage } = usePages();
	const { data: signatories, isLoading: signatoriesIsLoading } =
		useFetchPackageSignersQuery(applicationId);

	const [configureBankGiroMutation, { isLoading: configureBankGiroIsLoading }] =
		useSelectAndConfigureBankGiroMutation();

	const [configureCreditProducts] = useConfigureCreditProductsMutation({
		fixedCacheKey: "configureCreditProducts",
	});

	const [configureCheckCreditMutation] = useConfigureCheckCreditMutation();

	const [bankGiroFormValues, setBankGiroFormValues] = useState<Partial<BankGiroFormValues>>({});

	useEffect(() => {
		setBankGiroFormValues(getCurrentBankgiroFormValues(steps));
	}, [steps]);

	const configureBankGiro = async (config: Required<BankGiroFormValues>) => {
		// temporary until all packages contain bankgiro product:
		const hasBankgiroProduct = selectedProducts?.some(
			(p) => !p.isAddon && p.productDefinitionId === "BANKGIRO",
		);

		const completedStep = steps
			?.filter((s) => s.status === "Completed")
			.find(stepIs("ConfigureBankGiroProductStep"));

		const completedStepConfig = completedStep?.state.configuration;

		if (completedStepConfig !== config.answer) {
			const values: BankGiroConfig =
				config.answer === "transferExisting"
					? {
							transferExisting: config.existingNumber.replace("-", ""),
						}
					: config.answer;

			await configureBankGiroMutation({
				applicationId,
				config: values,
				existingId: completedStep?.state.productId,
				hasBankgiroProduct,
			}).unwrap();
		}

		if (!nextPage) throw new Error("Tried navigating to nextPage without nextPage available");

		navigate(nextPage);
	};

	const handleCreditProduct = async (config: CreditProductConfig[]) => {
		await configureCreditProducts({ applicationId, config }).unwrap();
	};

	const configureCheckCredit = async (config: { accountId: string; productId: string }) => {
		await configureCheckCreditMutation({ applicationId, config }).unwrap();
	};

	return {
		availablePackages,
		steps,
		areStepsCompleted,
		creditScore,
		isLoading:
			availablePackagesIsLoading ||
			isStepsLoading ||
			selectPackageIsLoading ||
			signatoriesIsLoading,
		selectedPackageId,
		selectedCreditProducts,
		selectedPackageFee,
		useSelectAddonMutation,
		useRemoveAddonMutation,
		useRemoveAndSelectPackageMutation,
		useRemovePackageMutation,
		useSelectPackageMutation,
		bankGiroFormValues,
		setBankGiroFormValues,
		configureBankGiro,
		selectedAddons,
		configureBankGiroIsLoading,
		configureCreditProducts,
		handleCreditProduct,
		configureCheckCredit,
		checkCreditProductBrackets,
		applicationId,
		signatories,
	};
};
